/**
 * Format a typeId (e.g. "product_service") to a display name (e.g. "Product Service").
 * @param {string} typeId
 * @returns {string}
 */
export function formatTypeIdToDisplayName(typeId) {
  if (!typeId) return "";
  return typeId
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Get the default block name for a layout item.
 * If the item has block_name, return it. Otherwise generate from typeId with optional number suffix.
 * e.g. single "product_service" -> "Product Service", multiple -> "Product Service 1", "Product Service 2"
 * @param {Array} layout - full layout array
 * @param {Object} item - layout item with i, typeId, and optionally block_name
 * @returns {string}
 */
export function getDefaultBlockName(layout, item) {
  if (!item) return "";
  if (item.block_name && String(item.block_name).trim()) {
    return String(item.block_name).trim();
  }
  const typeId = item.typeId;
  const sameTypeItems = layout.filter((l) => l.typeId === typeId);
  const displayBase = formatTypeIdToDisplayName(typeId);
  if (sameTypeItems.length <= 1) {
    return displayBase;
  }
  const index = sameTypeItems.findIndex((l) => l.i === item.i);
  return `${displayBase} ${index + 1}`;
}

/**
 * Resolve a wizard/template block id to its current layout name.
 * Template items use layout names; Add Block nodes use their stored block_name.
 */
export function getWizardBlockName(blockId, layout = [], nodes = []) {
  if (blockId == null || blockId === "") return "";
  const layoutItem = (layout || []).find((item) => String(item?.i) === String(blockId));
  if (layoutItem) return getDefaultBlockName(layout, layoutItem);

  const addNode = (nodes || []).find(
    (node) =>
      node?.type === "actionNode" &&
      node?.data?.nodeProps?.action === "add_block" &&
      String(node?.data?.nodeProps?.answers?.block_layout_item_id) === String(blockId)
  );
  return String(addNode?.data?.nodeProps?.answers?.block_name || "").trim();
}

/**
 * Edit Block names that actually rename a block. Unchanged names are omitted so
 * Add Block can keep using the original name of the block being edited.
 */
export function getRenamedEditBlockNamesLower(nodes = [], { currentActionId, layout = [] } = {}) {
  return (nodes || [])
    .filter(
      (node) =>
        node?.type === "actionNode" &&
        node?.data?.nodeProps?.action === "edit_block" &&
        node?.data?.nodeProps?.id !== currentActionId
    )
    .map((node) => {
      const answers = node?.data?.nodeProps?.answers || {};
      const name = String(answers.block_name || "").trim();
      if (!name) return "";
      const originalName = getWizardBlockName(answers.block_to_edit, layout, nodes);
      if (originalName && name.toLowerCase() === originalName.toLowerCase()) return "";
      return name.toLowerCase();
    })
    .filter(Boolean);
}

